import { create } from 'zustand'
import { AppState, ContactFormData, FormErrors } from './types'

const initialContactFormData: ContactFormData = {
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
    consent: false,
}

// Form validation functions
const validateEmail = (email: string): string | undefined => {
    if (!email.trim()) return 'Email is required'
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) return 'Please enter a valid email address'
    return undefined
}

const validateName = (name: string): string | undefined => {
    if (!name.trim()) return 'Name is required'
    if (name.trim().length < 2) return 'Name must be at least 2 characters'
    return undefined
}

const validatePhone = (phone: string): string | undefined => {
    if (phone && phone.trim()) {
        const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/
        if (!phoneRegex.test(phone.replace(/[\s\-\(\)]/g, ''))) {
            return 'Please enter a valid phone number'
        }
    }
    return undefined
}

const validateMessage = (message: string): string | undefined => {
    if (!message.trim()) return 'Message is required'
    if (message.trim().length < 10) return 'Message must be at least 10 characters'
    return undefined
}

const validateConsent = (consent: boolean): string | undefined => {
    if (!consent) return 'You must agree to the privacy policy to continue'
    return undefined
}

const validateContactForm = (data: ContactFormData): FormErrors => {
    const errors: FormErrors = {}

    const nameError = validateName(data.name)
    if (nameError) errors.name = nameError

    const emailError = validateEmail(data.email)
    if (emailError) errors.email = emailError

    const phoneError = validatePhone(data.phone || '')
    if (phoneError) errors.phone = phoneError

    const messageError = validateMessage(data.message)
    if (messageError) errors.message = messageError

    const consentError = validateConsent(data.consent)
    if (consentError) errors.consent = consentError

    return errors
}

export const useAppStore = create<AppState>((set) => ({
    // Initial UI State
    mobileMenuOpen: false,
    contactFormOpen: false,

    // Initial Form State
    contactForm: {
        data: initialContactFormData,
        errors: {},
        isSubmitting: false,
        isSubmitted: false,
        submitAttempts: 0,
        lastSubmissionTime: null,
        successMessage: null,
    },

    // Initial Analytics State
    trackingConsent: null,

    // UI Actions
    toggleMobileMenu: () => set((state) => ({
        mobileMenuOpen: !state.mobileMenuOpen
    })),

    openContactForm: () => set({ contactFormOpen: true }),

    closeContactForm: () => set({ contactFormOpen: false }),

    // Form Actions
    updateContactForm: (data: Partial<ContactFormData>) => set((state) => {
        const updatedData = { ...state.contactForm.data, ...data }
        // Clear errors for fields being updated (don't validate on change, only on blur/submit)
        const errors = { ...state.contactForm.errors }

        // Remove errors for fields being updated
        if (data.name !== undefined) delete errors.name
        if (data.email !== undefined) delete errors.email
        if (data.phone !== undefined) delete errors.phone
        if (data.message !== undefined) delete errors.message
        if (data.consent !== undefined) delete errors.consent

        return {
            contactForm: {
                ...state.contactForm,
                data: updatedData,
                errors,
            }
        }
    }),

    submitContactForm: async () => {
        const state = useAppStore.getState()
        const formData = state.contactForm.data
        const currentTime = Date.now()

        // Rate limiting: prevent spam submissions (minimum 5 seconds between attempts)
        if (state.contactForm.lastSubmissionTime &&
            currentTime - state.contactForm.lastSubmissionTime < 5000) {
            set((state) => ({
                contactForm: {
                    ...state.contactForm,
                    errors: {
                        general: 'Please wait a moment before submitting again.'
                    },
                }
            }))
            return
        }

        // Validate entire form before submission
        const validationErrors = validateContactForm(formData)

        if (Object.keys(validationErrors).length > 0) {
            set((state) => ({
                contactForm: {
                    ...state.contactForm,
                    errors: validationErrors,
                    submitAttempts: state.contactForm.submitAttempts + 1,
                }
            }))
            return
        }

        set((state) => ({
            contactForm: {
                ...state.contactForm,
                isSubmitting: true,
                errors: {},
                successMessage: null,
                lastSubmissionTime: currentTime,
            }
        }))

        try {
            // Import Google Sheets service dynamically
            const { submitToGoogleSheets } = await import('../apis/googleSheets')

            // Submit to Google Sheets
            const result = await submitToGoogleSheets({
                name: formData.name,
                email: formData.email,
                phone: formData.phone,
                company: formData.company,
                message: formData.message,
                consent: formData.consent,
            })

            if (result.success) {
                // Success handling
                set((state) => ({
                    contactForm: {
                        ...state.contactForm,
                        isSubmitting: false,
                        isSubmitted: true,
                        successMessage: result.message,
                        submitAttempts: 0, // Reset attempts on success
                    }
                }))

                // Auto-clear success message after 10 seconds
                setTimeout(() => {
                    set((state) => ({
                        contactForm: {
                            ...state.contactForm,
                            successMessage: null,
                        }
                    }))
                }, 10000)
            } else {
                throw new Error(result.message)
            }

        } catch (error) {
            const state = useAppStore.getState()
            const attempts = state.contactForm.submitAttempts + 1

            let errorMessage = 'Failed to submit form. Please try again.'

            if (error instanceof Error) {
                errorMessage = error.message

                // Provide helpful suggestions based on attempt count
                if (attempts >= 3) {
                    errorMessage += ' If this problem persists, please contact us directly at contact@setgo.in'
                } else if (attempts >= 2) {
                    errorMessage += ' Please check your internet connection and try again.'
                }
            }

            set((state) => ({
                contactForm: {
                    ...state.contactForm,
                    isSubmitting: false,
                    submitAttempts: attempts,
                    errors: {
                        general: errorMessage
                    },
                }
            }))
        }
    },

    // Form Reset and Utility Actions
    resetContactForm: () => set({
        contactForm: {
            data: initialContactFormData,
            errors: {},
            isSubmitting: false,
            isSubmitted: false,
            submitAttempts: 0,
            lastSubmissionTime: null,
            successMessage: null,
        }
    }),

    clearContactFormErrors: () => set((state) => ({
        contactForm: {
            ...state.contactForm,
            errors: {},
        }
    })),

    validateContactFormField: (field: keyof ContactFormData) => set((state) => {
        const data = state.contactForm.data
        const errors = { ...state.contactForm.errors }

        switch (field) {
            case 'name':
                const nameError = validateName(data.name)
                if (nameError) errors.name = nameError
                else delete errors.name
                break
            case 'email':
                const emailError = validateEmail(data.email)
                if (emailError) errors.email = emailError
                else delete errors.email
                break
            case 'phone':
                const phoneError = validatePhone(data.phone || '')
                if (phoneError) errors.phone = phoneError
                else delete errors.phone
                break
            case 'message':
                const messageError = validateMessage(data.message)
                if (messageError) errors.message = messageError
                else delete errors.message
                break
            case 'consent':
                const consentError = validateConsent(data.consent)
                if (consentError) errors.consent = consentError
                else delete errors.consent
                break
        }

        return {
            contactForm: {
                ...state.contactForm,
                errors,
            }
        }
    }),

    // Additional Form Actions
    setContactFormSuccess: (message: string) => set((state) => ({
        contactForm: {
            ...state.contactForm,
            successMessage: message,
            isSubmitted: true,
        }
    })),

    clearContactFormSuccess: () => set((state) => ({
        contactForm: {
            ...state.contactForm,
            successMessage: null,
        }
    })),

    // Analytics Actions
    setTrackingConsent: (consent: boolean) => set({ trackingConsent: consent }),
}))